import type { ContextLegend } from "../Components/ReportComponent/model/types";
import { mockLayers } from "../global/constants/layers";
import { buildArcgisClassLegend, type LayerUnit } from "./arcgisLegend";
import { geoServerBaseUrl, geoServerLegendJsonParams } from "./geoServer";

export interface WmsClassLegendItem {
  label: string;
  // Uma das duas: cor sólida (SLD) ou a imagem do símbolo (legenda ArcGIS).
  color?: string;
  imageUrl?: string;
}

interface WmsLayerRef {
  workspace: string;
  layerName: string;
}

// Legendas de camadas `type: "wms"` vêm do SLD do servidor — o front não tem
// estilo para elas. Cache por camada porque a resposta é estática e o mesmo
// pedido é feito pelo painel de legendas, pelo drawer e pelo relatório.
const cache = new Map<string, WmsClassLegendItem[]>();

// Vale para SLD, WFS e ArcGIS: sem limite, um endpoint travado seguraria a
// legenda inteira (e o relatório que a aguarda), inclusive o fallback SLD que
// já está pronto. Os pedidos correm em paralelo, então o pior caso é um só.
const LEGEND_REQUEST_TIMEOUT_MS = 8000;

async function fetchJson(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function symbolizerColor(symbolizer: any): string | null {
  if (!symbolizer) return null;
  const graphicFill = symbolizer.Point?.graphics?.[0]?.fill;
  return (
    symbolizer.Polygon?.fill ??
    symbolizer.Polygon?.stroke ??
    symbolizer.Line?.stroke ??
    graphicFill ??
    symbolizer.Point?.fill ??
    null
  );
}

/** Converte a resposta JSON do GetLegendGraphic em uma classe por regra do SLD. */
export function parseWmsClassLegend(data: any): WmsClassLegendItem[] {
  const rules: any[] = data?.Legend?.[0]?.rules ?? [];
  const items: WmsClassLegendItem[] = [];
  const seen = new Set<string>();

  rules.forEach((rule) => {
    const label: string = rule?.title ?? rule?.name ?? "";
    const color = symbolizerColor(rule?.symbolizers?.[0]);
    if (!label || !color || seen.has(label)) return;
    seen.add(label);
    items.push({ label, color });
  });

  return items;
}

export function getCachedWmsClassLegend({
  workspace,
  layerName,
}: WmsLayerRef): WmsClassLegendItem[] | undefined {
  return cache.get(`${workspace}:${layerName}`);
}

// Resolve `null` em qualquer falha: rede, HTTP, corpo inválido, timeout ou
// abort. O race com o abort garante o retorno mesmo se o fetch ignorar o sinal.
function fetchJsonWithTimeout(
  url: string,
  controller: AbortController = new AbortController()
): Promise<any | null> {
  const timer = setTimeout(() => controller.abort(), LEGEND_REQUEST_TIMEOUT_MS);
  const aborted = new Promise<null>((resolve) =>
    controller.signal.addEventListener("abort", () => resolve(null))
  );
  const request = fetchJson(url, { signal: controller.signal }).catch(
    () => null
  );
  return Promise.race([request, aborted]).finally(() => clearTimeout(timer));
}

function fetchSldJson({ workspace, layerName }: WmsLayerRef) {
  const params = new URLSearchParams({
    ...geoServerLegendJsonParams,
    layer: `${workspace}:${layerName}`,
  });
  return fetchJsonWithTimeout(
    `${geoServerBaseUrl}/${workspace}/wms?${params}`
  );
}

// Só os atributos (sem geometria) de todas as feições: a legenda precisa saber
// quais unidades existem no recorte inteiro, não só no viewport.
async function fetchLayerUnits(
  { workspace, layerName }: WmsLayerRef,
  siglaField: string
): Promise<LayerUnit[] | null> {
  const params = new URLSearchParams({
    service: "WFS",
    version: "1.0.0",
    request: "GetFeature",
    typeName: `${workspace}:${layerName}`,
    propertyName: `${siglaField},nome`,
    outputFormat: "application/json",
  });
  const data = await fetchJsonWithTimeout(
    `${geoServerBaseUrl}/${workspace}/wfs?${params}`
  );
  if (!data) return null;
  const units = new Map<string, LayerUnit>();
  (data?.features ?? []).forEach((feature: any) => {
    const sigla = feature?.properties?.[siglaField];
    if (sigla && !units.has(sigla)) {
      units.set(sigla, { sigla, nome: feature.properties.nome });
    }
  });
  return Array.from(units.values());
}

// O ArcGIS responde 200 com `{ error }` no corpo: também conta como falha.
async function fetchArcgisJson(url: string, controller: AbortController) {
  const json = await fetchJsonWithTimeout(url, controller);
  return json?.error ? null : json;
}

async function fetchArcgisClassLegend(
  ref: WmsLayerRef,
  { url, siglaField }: { url: string; siglaField: string }
): Promise<{ items: WmsClassLegendItem[]; cacheable: boolean }> {
  const arcgisController = new AbortController();
  const arcgisRequest = fetchArcgisJson(url, arcgisController);
  const [units, sldJson] = await Promise.all([
    fetchLayerUnits(ref, siglaField),
    fetchSldJson(ref),
  ]);

  const sldLegend = parseWmsClassLegend(sldJson);
  // Sem as unidades não dá para cruzar com o ArcGIS: cai na legenda SLD pura
  // sem esperar o ArcGIS, e sem cachear, para tentar a legenda completa de novo
  // na próxima abertura.
  if (!units) {
    arcgisController.abort();
    return { items: sldLegend, cacheable: false };
  }

  const arcgisJson = await arcgisRequest;
  // SLD rules are titled by sigla, so this doubles as the fallback color map
  // for units the ArcGIS legend doesn't cover (or when it is unreachable).
  const sldColors = new Map(sldLegend.map((item) => [item.label, item.color!]));
  return {
    items: buildArcgisClassLegend(arcgisJson, units, sldColors),
    // Com qualquer fonte falhando a legenda é degradada (só cores do SLD, ou
    // sem as unidades que dependem dele): não cacheia para tentar de novo.
    cacheable: arcgisJson !== null && sldJson !== null,
  };
}

export async function fetchWmsClassLegend(
  ref: WmsLayerRef
): Promise<WmsClassLegendItem[]> {
  const cacheKey = `${ref.workspace}:${ref.layerName}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const arcgisLegend = mockLayers.find(
    (l) => l.layerName === ref.layerName
  )?.arcgisLegend;

  try {
    const { items, cacheable } = arcgisLegend
      ? await fetchArcgisClassLegend(ref, arcgisLegend)
      : {
          items: parseWmsClassLegend(await fetchSldJson(ref)),
          cacheable: true,
        };
    // Falha transitória não é cacheada: cachear vazio esconderia a legenda até
    // um reload completo.
    if (cacheable && items.length > 0) cache.set(cacheKey, items);
    return items;
  } catch {
    return [];
  }
}

/**
 * Preenche as legendas das camadas `type: "wms"` do relatório com as classes do
 * SLD. Sem isso o relatório mostraria apenas um item genérico com o título da
 * camada, sem explicar as cores do mapa.
 */
export async function hydrateWmsLegends(
  legends: ContextLegend[]
): Promise<ContextLegend[]> {
  const targets = legends
    .map((legend) => mockLayers.find((l) => l.title === legend.layerName))
    .filter(
      (layer): layer is NonNullable<typeof layer> =>
        Boolean(layer) &&
        layer!.type === "wms" &&
        Boolean(layer!.workspace) &&
        !layer!.manualLegend
    );

  if (targets.length === 0) return legends;

  const byTitle = new Map<string, WmsClassLegendItem[]>();
  await Promise.all(
    targets.map(async (layer) => {
      const items = await fetchWmsClassLegend({
        workspace: layer.workspace as string,
        layerName: layer.layerName,
      });
      if (items.length > 0) byTitle.set(layer.title, items);
    })
  );

  return legends.map((legend) => {
    const items = byTitle.get(legend.layerName);
    if (!items) return legend;
    return {
      ...legend,
      items: items.map((item) => ({
        label: item.label,
        countUnavailable: true,
        style: item.imageUrl
          ? { type: "icon" as const, icon: item.imageUrl }
          : {
              type: "polygon" as const,
              color: item.color,
              fillColor: item.color,
              fillOpacity: 0.85,
              weight: 1,
            },
      })),
    };
  });
}
